import { Flex } from '@chakra-ui/react';
import React from 'react';
import { render } from 'storyblok-rich-text-react-renderer';
import { useShallow } from 'zustand/react/shallow';

import CustomCheckboxGroup from '@/common/CustomCheckboxGroup/CustomCheckboxGroup';
import CustomCombobox from '@/common/CustomCombobox/CustomCombobox';
import CustomFormCheckbox from '@/common/CustomFormCheckbox/CustomFormCheckbox';
import CustomFormPageTitleAndDescription from '@/common/CustomFormPageTitleAndDescription/CustomFormPageTitleAndDescription';
import CustomInput from '@/common/CustomInput/CustomInput';
import CustomInputGroup from '@/common/CustomInputGroup/CustomInputGroup';
import CustomRadioButtonGroup from '@/common/CustomRadioButtonGroup/CustomRadioButtonGroup';
import CustomTextArea from '@/common/CustomTextArea/CustomTextArea';
import TitleAndContent from '@/common/storyblok/TitleAndContent/TitleAndContent';
import { useUserInfoStore } from '@/stores/UserInfoStore';
import './CustomStoryblokForm.scss';

interface CustomStoryblokFormProps {
  formPageArray: any;
  formPageName?: string;
  isStandaloneForm: boolean;
  steps: { title: string }[];
  activeStep: number;
  goBackToSelectedStep: (step: number) => void;
}

//////////////////////////////////////
// Form Components Map
//////////////////////////////////////

const formComponentsMap: { [key: string]: any } = {
  // Form Components
  'Input Group': CustomInputGroup,
  Input: CustomInput,
  'Input Email': CustomInput,
  'Input Number': CustomInput,
  'Input URL': CustomInput,
  'Text Area': CustomTextArea,
  'Radio Button': CustomRadioButtonGroup,
  'Checkbox Group': CustomCheckboxGroup,
  Checkbox: CustomFormCheckbox,
  Dropdown: CustomCombobox,

  // Storyblok Components
  'Title And Content': TitleAndContent,
};

//////////////////////////////////////
// Component Look Up Function
//////////////////////////////////////

const componentLookUp = ({
  formPageName,
  formComponentName,
  name,
  description,
  label,
  placeholder,
  isRequired,
  radioButtonOptions,
  checkboxOptions,
  dropdownOptions,
  type,
  maxNumberOfSelections,
  isDisabled,
  titleAndContentStoryblok,
  inputGroup,
}: {
  formPageName: string;
  formComponentName: string;
  name: string;
  description: string;
  label: string;
  placeholder: string;
  isRequired: boolean;
  radioButtonOptions: string[];
  checkboxOptions: string[];
  dropdownOptions: string[];
  type: string;
  maxNumberOfSelections: number;
  isDisabled: boolean;
  titleAndContentStoryblok: {
    Alignment: string;
    Title: string;
    Content: string;
  };
  inputGroup: any[];
}) => {
  const ComponentFunction = formComponentsMap[formComponentName];

  if (!ComponentFunction) return <></>;

  //////////////////////////////////////
  // Input Group component
  // - Render Input component within CustomInputGroup
  //////////////////////////////////////

  if (formComponentName === 'Input Group' && inputGroup.length > 0) {
    return (
      <CustomInputGroup>
        {inputGroup.map(inputProperties => {
          const InputComponent = formComponentsMap[inputProperties.component];

          return (
            InputComponent && (
              <InputComponent
                key={`${formPageName}.${inputProperties.Name}`}
                name={`${formPageName}.${inputProperties.Name}`}
                label={inputProperties.Label}
                placeholder={inputProperties.Placeholder}
                description={render(inputProperties.Description)}
                isRequired={inputProperties.Required}
              />
            )
          );
        })}
      </CustomInputGroup>
    );
  }

  //////////////////////////////////////
  // Other components
  //////////////////////////////////////

  const componentProps = {
    name,
    description,
    label,
    placeholder,
    isRequired,
    radioButtonOptions,
    checkboxOptions,
    dropdownOptions,
    maxNumberOfSelections,
    type,
    isDisabled,
    blok: titleAndContentStoryblok,
  };

  return <ComponentFunction {...componentProps} />;
};

//////////////////////////////////////
// Custom Storyblok Form Component
//////////////////////////////////////

const CustomStoryblokForm = ({
  formPageArray,
  formPageName = '',
  isStandaloneForm,
  steps,
  activeStep,
  goBackToSelectedStep,
}: CustomStoryblokFormProps) => {
  const [isLoggedInWithName] = useUserInfoStore(
    useShallow(state => [state.isLoggedInWithName])
  );

  //////////////////////////////////////
  // Form Components from Storyblok
  //////////////////////////////////////

  const formPage = formPageArray[0];

  const pageTitle = formPage.PageTitle;
  const pageDescription = isStandaloneForm
    ? render(formPage.PageDescriptionStandalone)
    : render(formPage.PageDescriptionCTA);

  const formFields = formPage.PageLayout;

  return (
    <Flex className={'custom-storyblok-form-page-container'}>
      <CustomFormPageTitleAndDescription
        title={pageTitle}
        description={pageDescription}
        isStandaloneForm={isStandaloneForm}
        steps={steps}
        activeStep={activeStep}
        goBackToSelectedStep={goBackToSelectedStep}
      />

      {formFields && (
        <Flex className={'custom-storyblok-form-container'}>
          {formFields.map((field: any) => {
            const name = `${formPageName}.${field.Name}`;
            const description = render(field.Description);
            const label = field.Label;
            const formComponentName = field.component;
            const placeholder = field.Placeholder;
            const isRequired = field.Required;
            const isDisabled = field.Disabled;
            const type =
              formComponentName === 'Input Number' ? 'number' : 'text';

            ///////////////////////////////////////////
            // Input (Email)
            ///////////////////////////////////////////

            const isEmailFieldDisabled =
              field.Name === 'email' && isLoggedInWithName;

            ///////////////////////////////////////////
            // Radio Button
            ///////////////////////////////////////////

            const radioButtonOptionsFromStoryblok = field.RadioButtonOptions;

            const radioButtonOptions = radioButtonOptionsFromStoryblok?.map(
              (option: any) => option.RadioButtonOption
            );

            ///////////////////////////////////////////
            // Checkbox Group
            ///////////////////////////////////////////

            const maxNumberOfSelections = field.MaxNumberOfSelections;
            const checkboxOptionsFromStoryblok = field.CheckboxOptions;

            const checkboxOptions = checkboxOptionsFromStoryblok?.map(
              (option: any) => {
                const name = option.Name;
                const label = option.Label;

                return {
                  name,
                  label,
                };
              }
            );

            ///////////////////////////////////////////
            // Checkbox
            ///////////////////////////////////////////

            const hideSaveContactDetailsCheckbox =
              field.Name === 'saveContactDetails' && !isLoggedInWithName;

            ///////////////////////////////////////////
            // Dropdown
            ///////////////////////////////////////////

            const dropdownFieldsMapping: { [key: string]: any } = {
              salutation: field.DropdownOptions,
              industry: field.DropdownOptions,
              jobLevel: field.DropdownOptions,
              jobFunction: field.DropdownOptions,
              companyHqLocation: field.DropdownOptions,
              placeOfResidence: field.DropdownOptions,
            };

            const dropdownOptionsFromStoryblok = field.DropdownOptions;

            const dropdownOptionsDatasource =
              dropdownFieldsMapping[field.Name]?.[0]?.DropdownOptions;

            const dropdownOptionsCustom = dropdownOptionsFromStoryblok?.map(
              (option: any) => option.DropdownOption
            );

            const dropdownOptions = dropdownFieldsMapping[field.Name]
              ? dropdownOptionsDatasource
              : dropdownOptionsCustom;

            ///////////////////////////////////////////
            // Title and Content
            ///////////////////////////////////////////

            const alignment = field.Alignment;
            const title = field.Title;
            const content = field.Content;

            const titleAndContentStoryblok = {
              Alignment: alignment,
              Title: title,
              Content: content,
            };

            ///////////////////////////////////////////
            // Input Group
            ///////////////////////////////////////////

            const inputGroup = field.InputGroup;

            ///////////////////////////////////////////
            // Hide Save Contact Details checkbox if the user is logged in
            ///////////////////////////////////////////

            if (hideSaveContactDetailsCheckbox) return null;

            return (
              <Flex
                key={`${field.Name ?? label ?? title}`}
                className={'form-field-container'}
              >
                {componentLookUp({
                  formPageName,
                  formComponentName,
                  name,
                  description,
                  label,
                  placeholder,
                  isRequired,
                  radioButtonOptions,
                  checkboxOptions,
                  dropdownOptions,
                  type,
                  maxNumberOfSelections,
                  isDisabled: isDisabled ?? isEmailFieldDisabled,
                  titleAndContentStoryblok,
                  inputGroup,
                })}
              </Flex>
            );
          })}
        </Flex>
      )}
    </Flex>
  );
};

export default CustomStoryblokForm;
